// Un producto del plan (ADR-2026-10-14-el-catalogo-arranca-con-el-agente-a-pantalla-completa):
// una tarjeta siempre abierta, sin divisores, con su foto, su nombre y al lado "Datos" como
// pastilla con chevron (medidas + características + notas, en un panel adentro de la misma
// tarjeta), un tacho para sacarlo y
// un cuadro por toma con su título debajo. Los datos son UNA lista de campos —medidas y
// características— con las notas abajo (`CamposDelProducto`,
// ADR-2026-10-15-los-datos-del-producto-son-una-lista). Mientras el agente todavía no armó las tomas, el lugar de los
// cuadros late.
import { ChevronDown, ClipboardList, Trash2 } from 'lucide-react';
import { useState } from 'react';
import CachedImg from '../../../../components/CachedImg';
import type { FichaDeProducto } from '../../../../lib/fichaDeProducto';
import type { Medidas } from '../../../../lib/medidas';
import { CamposDelProducto } from './CamposDelProducto';
import type { ItemDelPlan, PlanDeCatalogo, ProductoDelPlan } from './plan';
import { ANCHO_TARJETA, TarjetaDeToma } from './TarjetaDeToma';

export interface BloqueDeProductoProps {
  producto: ProductoDelPlan;
  tomas: PlanDeCatalogo['tomas'];
  /** Lo que `queFalta` dice de este producto, si algo. */
  falta: string | null;
  itemAbierto: string | null;
  miniaturaDe: (item: ItemDelPlan) => string | null;
  /** La proporción del formato del catálogo, como CSS ("4 / 5"). */
  aspecto: string;
  onAbrirItem: (id: string) => void;
  /** Una toma con la foto ya salida: verla ampliada. */
  onVerImagen?: (imageId: string) => void;
  /** Sacar la edición o el rehacer pendiente de una toma. */
  onQuitarPendiente?: (itemId: string) => void;
  onMedidas: (medidas: Medidas) => void;
  onFicha: (ficha: FichaDeProducto) => void;
  onNotas: (notas: string) => void;
  onQuitar: () => void;
  /** El escritor está escribiendo (las tomas sin prompt lo muestran). */
  escribiendo?: boolean;
}

export const BloqueDeProducto: React.FC<BloqueDeProductoProps> = ({
  producto,
  tomas,
  falta,
  itemAbierto,
  miniaturaDe,
  aspecto,
  onAbrirItem,
  onVerImagen,
  onQuitarPendiente,
  onMedidas,
  onFicha,
  onNotas,
  onQuitar,
  escribiendo = false,
}) => {
  const [datos, setDatos] = useState(false);
  const tomaDe = (item: ItemDelPlan) => tomas.find((t) => t.id === item.tomaId);
  const verDe = (item: ItemDelPlan) => {
    const id = item.imageIds?.[0];
    return onVerImagen && id ? () => onVerImagen(id) : undefined;
  };
  const exigeMedidas = producto.items.some((i) => {
    const t = tomaDe(i);
    return t?.icono === 'medidas' || !!t?.infografia;
  });

  return (
    <section
      className={`flex flex-col gap-4 rounded-[28px] bg-white p-4 shadow-[0_1px_2px_rgb(var(--brand-black-rgb)_/_0.04),0_8px_24px_-12px_rgb(var(--brand-black-rgb)_/_0.12)] ring-1 ${
        falta ? 'ring-[#F3C3BE]' : 'ring-black/[0.05]'
      }`}
    >
      <div className="flex items-center gap-3">
        {producto.portadaUrl ? (
          <CachedImg src={producto.portadaUrl} alt="" className="h-12 w-12 shrink-0 rounded-xl bg-[#F5F6FA] object-cover sm:h-14 sm:w-14" />
        ) : (
          <div className="h-12 w-12 shrink-0 rounded-xl bg-[#F5F6FA] sm:h-14 sm:w-14" aria-hidden="true" />
        )}
        <div className="min-w-0 flex-1">
          {/* "Datos" va pegado al nombre y despliega el panel; el chevron dice si está abierto. Los
              datos ya no se resumen abajo del nombre: están en el panel
              (ADR-2026-10-15-el-bloque-de-producto-se-ordena). Lo que sí queda es lo que FALTA. */}
          <div className="flex min-w-0 items-center gap-2.5">
            <p className="truncate text-[15px] font-semibold text-brand-black sm:text-[16px]">{producto.nombre}</p>
            <button
              type="button"
              onClick={() => setDatos((v) => !v)}
              aria-expanded={datos}
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-full py-1.5 pl-3 pr-2.5 text-[13px] font-semibold transition-colors ${
                datos ? 'bg-[rgb(var(--brand-primary-rgb)_/_0.095)] text-brand-primary' : 'bg-[#F1F3F9] text-brand-black hover:bg-[#E6E9F2]'
              }`}
            >
              <ClipboardList className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden="true" />
              Datos
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform duration-200 ${datos ? 'rotate-180' : ''}`}
                strokeWidth={2.25}
                aria-hidden="true"
              />
            </button>
          </div>
          {falta && <p className="truncate text-[13px] text-[#B42318]">{falta}</p>}
        </div>
        {/* Un tacho y no una X: sacar el producto saca sus tomas del catálogo. */}
        <button
          type="button"
          onClick={onQuitar}
          aria-label={`Sacar ${producto.nombre} del catálogo`}
          title="Sacar del catálogo"
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-[rgb(var(--brand-black-rgb)_/_0.4)] transition-colors hover:bg-black/[0.04] hover:text-[#B42318]"
        >
          <Trash2 className="h-4 w-4" strokeWidth={2} />
        </button>
      </div>

      {datos && (
        <div className="animate-fade-in flex flex-col gap-3 rounded-xl bg-[#F5F6FA] p-4">
          <CamposDelProducto medidas={producto.medidas} ficha={producto.ficha} exigeMedidas={exigeMedidas} onMedidas={onMedidas} onFicha={onFicha} />
          <div>
            <p className="mb-1.5 text-[13px] font-semibold text-brand-black">Notas para este producto</p>
            <textarea
              value={producto.notas ?? ''}
              onChange={(e) => onNotas(e.target.value)}
              rows={2}
              placeholder="Lo que el escritor tiene que saber de este producto en particular."
              className="w-full resize-y rounded-lg border border-black/[0.1] bg-white px-2.5 py-1.5 text-[13.5px] text-brand-black"
            />
          </div>
        </div>
      )}

      {/* Sin tomas todavía (el agente está armando el plan): el lugar de los cuadros late. */}
      {tomas.length === 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:gap-4" aria-hidden="true">
          {[0, 1, 2].map((n) => (
            <div key={n} className={`flex flex-col gap-2 ${ANCHO_TARJETA}`}>
              <span className="block w-full animate-pulse rounded-xl bg-[#F1F3F9]" style={{ aspectRatio: aspecto, animationDelay: `${n * 150}ms` }} />
              <span className="block h-3 w-2/3 animate-pulse rounded bg-[#F1F3F9]" style={{ animationDelay: `${n * 150}ms` }} />
            </div>
          ))}
        </div>
      ) : producto.items.length === 0 ? (
        <p className="text-[13px] text-[#3C3C43]/60">Ninguna toma aplica a este producto.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:gap-4">
          {producto.items.map((item) => {
            const toma = tomaDe(item);
            return toma ? (
              <TarjetaDeToma
                key={item.id}
                toma={toma}
                item={item}
                miniatura={miniaturaDe(item)}
                activa={itemAbierto === item.id}
                aspecto={aspecto}
                escribiendo={escribiendo}
                onAbrir={() => onAbrirItem(item.id)}
                onVer={verDe(item)}
                onQuitarPendiente={onQuitarPendiente ? () => onQuitarPendiente(item.id) : undefined}
              />
            ) : null;
          })}
        </div>
      )}
    </section>
  );
};
